import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import i18n from '@/lang/i18n'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { initialTopographicSubsetLaboratoryGridState, TopographicSubsetLaboratoryGridState } from '@/modules/configurationModule/detail/local-samples/topographic-subset-laboratory/components/topographic-subset-laboratory-grid/topographic-subset-laboratory-grid.model'
import { ConfigurationTopographicSubsetLaboratoryCreateRequest, ConfigurationTopographicSubsetLaboratoryPatchInBatchRequest, ConfigurationTopographicSubsetLaboratoryResponse } from '@/modules/configurationModule/detail/local-samples/topographic-subset-laboratory/services/topographic-subset-laboratory.dto'
import { topographicSubsetLaboratoriesConfigurationServices } from '@/modules/configurationModule/detail/local-samples/topographic-subset-laboratory/services/topographic-subset-laboratory.service'
import LocalTopographicSubsetLaboratoryModal from '@/modules/configurationModule/detail/local-samples/topographic-subset-laboratory/components/topographic-subset-laboratory-modal/topographic-subset-laboratory-modal.vue'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        LocalTopographicSubsetLaboratoryModal
    }
})

export default class TopographicSubsetLaboratoryGrid extends BaseCrudServerSideComponent<ConfigurationTopographicSubsetLaboratoryResponse> implements BaseCrud {
    @Ref() readonly localTopographicSubsetLaboratoryModalWrapper!: any

    state: TopographicSubsetLaboratoryGridState
    constructor() {
        super()
        this.state = initialTopographicSubsetLaboratoryGridState()
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get canUpdate(): boolean {
        const numberRowSelected: number = this.state.selectedRows.length
        return numberRowSelected === 1
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.onDelete()
            },
            {
                name: params?.node?.data?.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.onActivate()
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.onDeactivate()
            }
        ]
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: ConfigurationTopographicSubsetLaboratoryResponse[]): Promise<void> {
        const filteredRowActiveState = selectionBatch?.filter((row: ConfigurationTopographicSubsetLaboratoryResponse) => row.active !== option)
        const updatedFilteredRowActiveState: ConfigurationTopographicSubsetLaboratoryPatchInBatchRequest[] = filteredRowActiveState?.map((row: ConfigurationTopographicSubsetLaboratoryResponse) => {
            return {
                id: row.id,
                active: option
            }
        })
        if (updatedFilteredRowActiveState?.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(topographicSubsetLaboratoriesConfigurationServices.patchTopographicSubsetLaboratory(this.state.labId, updatedFilteredRowActiveState), true)
            this.$_updateLoading(false)
            if (err) {
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async createOperation(row: ConfigurationTopographicSubsetLaboratoryCreateRequest): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(topographicSubsetLaboratoriesConfigurationServices.createTopographicSubsetLaboratory(this.state.labId, row), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.localTopographicSubsetLaboratoryModalWrapper.$slots.default[0].componentInstance.updateRow(response)
        this.state = {
            ...this.state,
            actionTitle: i18n.t('configuration.actions.update').toString()
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(rows: ConfigurationTopographicSubsetLaboratoryPatchInBatchRequest[]): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(topographicSubsetLaboratoriesConfigurationServices.patchTopographicSubsetLaboratory(this.state.labId, rows), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async deleteOperation(rows: ConfigurationTopographicSubsetLaboratoryResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this._deleteBatchOperation(rows)
            }
        }
    }

    onAdd(): void {
        this.showModal(this.$t('configuration.actions.add').toString(), null, this.localTopographicSubsetLaboratoryModalWrapper)
    }

    onUpdate(targetResponseTime: ConfigurationTopographicSubsetLaboratoryResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (targetResponseTime) {
            this.showModal(this.$t('configuration.actions.update').toString(), targetResponseTime, this.localTopographicSubsetLaboratoryModalWrapper)
        }
    }

    onDelete(): void {
        if (this.state.selectedRows.length) {
            this.deleteOperation(this.state.selectedRows)
        }
    }

    onActivate(): void {
        this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    onDeactivate(): void {
        this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    private async _deleteBatchOperation(rows: ConfigurationTopographicSubsetLaboratoryResponse[]): Promise<void> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((template: ConfigurationTopographicSubsetLaboratoryResponse) => Number(template?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(topographicSubsetLaboratoriesConfigurationServices.deleteTopographicSubsetLaboratory(this.state.labId, rowsToDelete), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (rows.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: rows?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }
}
